// SmartIntakePage — UX v0.7 three-step project creation + intake.
//
// Flow:
//   Step 1 · Describe   → project name, problem statement (T1), live
//                         detection of shape/regulatory/nouns
//   Step 2 · Shape      → project shape grid + industry vertical,
//                         with a recipe + agent roster preview
//   Step 3 · Delivery   → multi-select audience, review cadence (minimal /
//                         standard / thorough), output template mode
//
// On submit we:
//   1. POST /api/projects  (create the project)
//   2. POST /api/projects/{id}/intake (submit the legacy-shaped intake)
//   3. POST /api/projects/{id}/runs/1/briefings/1 (auto-emit briefing)
//   4. navigate to /projects/:id — the new Command Center

import { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as LucideIcons from 'lucide-react'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Edit3,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  ScanSearch,
  Send,
  Shapes,
  ShieldCheck,
  Sparkles,
  Workflow,
  X,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api'
import type {
  DeploymentCatalogField,
  DeploymentCatalogEntry,
} from '@/lib/api'
import { DeploymentField } from '@/components/intake/DeploymentField'
import { useUxStore } from '@/stores/uxStore'
import {
  formatRelativeAge,
  useIntakeDraftStore,
  type IntakeDraft,
} from '@/stores/intakeDraftStore'
import {
  clearPendingFiles,
  loadPendingFiles,
  savePendingFiles,
} from '@/lib/pendingFileStore'
import {
  AUDIENCE_OPTIONS,
  PROJECT_SHAPES,
  REGULATORY_CUES,
  collapseAudience,
  detectNouns,
  detectRegulatory,
  detectShape,
  getShape,
  type AudienceTag,
  type ShapeId,
} from '@/components/intake/shapes'
import { InputUploader, type PendingFile } from '@/components/intake/InputUploader'
import { SelectableCardGrid } from '@/components/intake/SelectableCardGrid'
import type {
  InterventionBudget,
  RegulatoryFlag,
} from '@/types'

// ---------------------------------------------------------------------- //

const VERTICALS = [
  'Healthcare',
  'Fintech',
  'Government',
  'Consumer',
  'Education',
  'Industrial / Robotics',
  'Gaming',
  'Media',
  'Defense',
  'Research',
  'Security / SecOps',
  'Scientific',
  'Web3 / Crypto',
  'Dev infra',
]

interface FormState {
  name: string
  problem_statement: string
  shape: ShapeId | null
  vertical: string | null
  regulatory: RegulatoryFlag[]
  /**
   * Once the user has manually edited the regulatory list (added or
   * removed a chip), the auto-detection effect stops overwriting it.
   * Without this flag, removing an auto-detected cue would just snap
   * back as soon as the detector re-fired.
   */
  regulatoryUserEdited: boolean
  audience: AudienceTag[]
  cadence: InterventionBudget
  /**
   * Document control — required by the backend's strict-mode intake
   * validator (`AI_ALM_INTAKE_OWNER_STRICT`, on by default) whenever
   * ANY non-`'none'` regulatory flag is set. SOC 2 / ISO 27001
   * reviewers need a named owner + approver before treating the
   * requirements package as `issued` vs `draft`. The per-role
   * signatory chain (CISO, DPO, …) is filled separately in the
   * Approval Chain editor inside the Command Center; these two are
   * the document-level owner/approver pair that lands in the header.
   */
  document_owner: string
  document_approver: string
  /**
   * AI-governance ratification. When the shape is `ml_ai` (or any
   * project whose requirements declare an AI-derived output), the
   * backend's `PEG-AI-GOVERNANCE-SIGNATURE-001` fires HIGH at export
   * unless `phase1/ai-governance-decision.json` carries a signed
   * disposition. Setting these intake fields populates that file at
   * phase 1, so the gate clears proactively. `null` disposition means
   * the architect has not yet ratified — left to the export-time gate
   * to surface.
   */
  ai_governance_disposition: 'in_scope' | 'not_applicable' | null
  ai_governance_ratified_by: string
  ai_governance_rationale: string
  /**
   * Deployment architecture — 10 fields the backend's
   * `PEG-DEPLOYMENT-MANIFEST-001` gate requires for SOC 2 CC7 / CC8
   * evidence. The Operations Architect agent's prompt says these
   * MUST appear verbatim in the manifest when intake declares them;
   * `deployment_manifest_postprocessor.fill_tbd_decisions_from_intake`
   * is the safety net behind that. Leaving any of these blank is a
   * valid "deliberately deferred" choice — the gate will then list
   * the missing key at Phase 5 and the architect can either fill it
   * post-hoc OR waive via `phase4/audit-diff.waivers.json`.
   */
  cloud_provider: string
  primary_region: string
  container_runtime: string
  orchestrator: string
  iac_framework: string
  secrets_store: string
  secrets_rotation_policy: string
  oncall_primary_rotation: string
  oncall_runbook_index: string
  oncall_pager_severity_thresholds: string
  /** Files queued in Step 1 to feed Phase 2 (Observer) once the project exists. */
  contentFiles: PendingFile[]
  /** Files queued in Step 1 to inform Phase 5 export formatting. */
  formatFiles: PendingFile[]
}

function initialForm(): FormState {
  return {
    name: '',
    problem_statement: '',
    shape: null,
    vertical: null,
    regulatory: [],
    regulatoryUserEdited: false,
    audience: ['engineers'],
    cadence: 'standard',
    document_owner: '',
    document_approver: '',
    ai_governance_disposition: null,
    ai_governance_ratified_by: '',
    ai_governance_rationale: '',
    cloud_provider: '',
    primary_region: '',
    container_runtime: '',
    orchestrator: '',
    iac_framework: '',
    secrets_store: '',
    secrets_rotation_policy: '',
    oncall_primary_rotation: '',
    oncall_runbook_index: '',
    oncall_pager_severity_thresholds: '',
    contentFiles: [],
    formatFiles: [],
  }
}

// ---------------------------------------------------------------------- //

export function SmartIntakePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setProjectShape = useUxStore((s) => s.setShape)
  const setProjectAudience = useUxStore((s) => s.setAudience)
  const setProjectVertical = useUxStore((s) => s.setVertical)

  // Draft store — see plan §P2.16. Auto-saves form fields to
  // localStorage on every change (debounced) so reload / accidental
  // exit / explicit "Save & exit" all preserve the work in progress.
  const persistedDraft = useIntakeDraftStore((s) => s.draft)
  const saveDraft = useIntakeDraftStore((s) => s.saveDraft)
  const clearDraft = useIntakeDraftStore((s) => s.clearDraft)

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [form, setForm] = useState<FormState>(initialForm)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // ``hasResumed`` toggles to ``true`` once the user accepts (or
  // dismisses) the "you have a saved draft" banner so we don't keep
  // re-prompting on every render.
  const [draftPrompt, setDraftPrompt] = useState<'pending' | 'resumed' | 'fresh'>(
    () => (persistedDraft && (persistedDraft.name.trim() || persistedDraft.problem_statement.trim()) ? 'pending' : 'fresh'),
  )

  // Live detection (debounced via input; recomputed on each render is fine,
  // the problem statement is small).
  const detected = useMemo(() => {
    const ps = form.problem_statement
    return {
      shape: detectShape(ps),
      regulatory: detectRegulatory(ps),
      nouns: detectNouns(ps),
    }
  }, [form.problem_statement])

  // When the user has typed enough, pre-select the detected shape if they
  // haven't picked one yet.
  useEffect(() => {
    if (!form.shape && detected.shape.shape) {
      setForm((f) => ({ ...f, shape: detected.shape.shape!.id }))
    }
  }, [detected.shape.shape, form.shape])

  // Auto-save the draft on every form change once the user has typed
  // anything meaningful. We debounce via a 600 ms idle window so we
  // don't pummel localStorage on every keystroke. The draft is only
  // persisted to localStorage when the user exits via the Save & Exit
  // affordance OR the auto-save fires, never during the submit
  // pipeline (which clears the draft instead).
  useEffect(() => {
    if (draftPrompt === 'pending') return // Wait for resume decision first.
    const handle = window.setTimeout(() => {
      const meaningful =
        form.name.trim().length > 0 || form.problem_statement.trim().length > 0
      if (meaningful) {
        saveDraft({
          name: form.name,
          problem_statement: form.problem_statement,
          shape: form.shape,
          vertical: form.vertical,
          regulatory: form.regulatory,
          regulatoryUserEdited: form.regulatoryUserEdited,
          audience: form.audience,
          cadence: form.cadence,
          document_owner: form.document_owner,
          document_approver: form.document_approver,
          ai_governance_disposition: form.ai_governance_disposition,
          ai_governance_ratified_by: form.ai_governance_ratified_by,
          ai_governance_rationale: form.ai_governance_rationale,
          cloud_provider: form.cloud_provider,
          primary_region: form.primary_region,
          container_runtime: form.container_runtime,
          orchestrator: form.orchestrator,
          iac_framework: form.iac_framework,
          secrets_store: form.secrets_store,
          secrets_rotation_policy: form.secrets_rotation_policy,
          oncall_primary_rotation: form.oncall_primary_rotation,
          oncall_runbook_index: form.oncall_runbook_index,
          oncall_pager_severity_thresholds: form.oncall_pager_severity_thresholds,
          step,
        })
      }
      // File objects can't live in the localStorage draft, so mirror the
      // queued uploads to IndexedDB under the same slot. Without this a
      // reload / resume silently drops attached docs (they never reach
      // Phase 2). Runs independently of `meaningful` so a file attached
      // before any text is typed still survives.
      void savePendingFiles('intake', {
        contentFiles: form.contentFiles,
        formatFiles: form.formatFiles,
      })
    }, 600)
    return () => window.clearTimeout(handle)
  }, [form, step, draftPrompt, saveDraft])

  // Apply a previously saved draft when the user clicks "Resume".
  const resumeDraft = () => {
    if (!persistedDraft) {
      setDraftPrompt('fresh')
      return
    }
    setForm((f) => ({
      ...f,
      name: persistedDraft.name,
      problem_statement: persistedDraft.problem_statement,
      shape: persistedDraft.shape,
      vertical: persistedDraft.vertical,
      regulatory: persistedDraft.regulatory,
      regulatoryUserEdited: persistedDraft.regulatoryUserEdited,
      audience: persistedDraft.audience.length > 0 ? persistedDraft.audience : f.audience,
      cadence: persistedDraft.cadence,
      document_owner: persistedDraft.document_owner ?? '',
      document_approver: persistedDraft.document_approver ?? '',
      ai_governance_disposition: persistedDraft.ai_governance_disposition ?? null,
      ai_governance_ratified_by: persistedDraft.ai_governance_ratified_by ?? '',
      ai_governance_rationale: persistedDraft.ai_governance_rationale ?? '',
      cloud_provider: persistedDraft.cloud_provider ?? '',
      primary_region: persistedDraft.primary_region ?? '',
      container_runtime: persistedDraft.container_runtime ?? '',
      orchestrator: persistedDraft.orchestrator ?? '',
      iac_framework: persistedDraft.iac_framework ?? '',
      secrets_store: persistedDraft.secrets_store ?? '',
      secrets_rotation_policy: persistedDraft.secrets_rotation_policy ?? '',
      oncall_primary_rotation: persistedDraft.oncall_primary_rotation ?? '',
      oncall_runbook_index: persistedDraft.oncall_runbook_index ?? '',
      oncall_pager_severity_thresholds:
        persistedDraft.oncall_pager_severity_thresholds ?? '',
    }))
    setStep(persistedDraft.step)
    // Restore queued uploads from IndexedDB alongside the text fields.
    void loadPendingFiles('intake').then((files) => {
      setForm((f) => ({
        ...f,
        contentFiles: files.contentFiles,
        formatFiles: files.formatFiles,
      }))
    })
    setDraftPrompt('resumed')
  }

  // Discard a saved draft and start fresh.
  const discardDraft = () => {
    clearDraft()
    void clearPendingFiles('intake')
    setDraftPrompt('fresh')
  }

  // "Save & exit" — explicit affordance for users who want to bounce
  // back to the Hub knowing their work is preserved.
  const saveAndExit = () => {
    saveDraft({
      name: form.name,
      problem_statement: form.problem_statement,
      shape: form.shape,
      vertical: form.vertical,
      regulatory: form.regulatory,
      regulatoryUserEdited: form.regulatoryUserEdited,
      audience: form.audience,
      cadence: form.cadence,
      document_owner: form.document_owner,
      document_approver: form.document_approver,
      ai_governance_disposition: form.ai_governance_disposition,
      ai_governance_ratified_by: form.ai_governance_ratified_by,
      ai_governance_rationale: form.ai_governance_rationale,
      cloud_provider: form.cloud_provider,
      primary_region: form.primary_region,
      container_runtime: form.container_runtime,
      orchestrator: form.orchestrator,
      iac_framework: form.iac_framework,
      secrets_store: form.secrets_store,
      secrets_rotation_policy: form.secrets_rotation_policy,
      oncall_primary_rotation: form.oncall_primary_rotation,
      oncall_runbook_index: form.oncall_runbook_index,
      oncall_pager_severity_thresholds: form.oncall_pager_severity_thresholds,
      step,
    })
    void savePendingFiles('intake', {
      contentFiles: form.contentFiles,
      formatFiles: form.formatFiles,
    })
    navigate('/projects')
  }

  // When the detector finds regulatory cues and the user hasn't manually
  // edited the list, surface the latest detection as pre-checked flags.
  // Once the user has explicitly added or removed a chip we stop touching
  // their list so a re-typed problem statement can't undo a deliberate
  // removal.
  useEffect(() => {
    if (form.regulatoryUserEdited) return
    const detectedFlags = detected.regulatory.map((r) => r.flag)
    setForm((f) => {
      // Only update if the detected set actually differs from current —
      // otherwise we'd thrash on every keystroke.
      const same =
        f.regulatory.length === detectedFlags.length &&
        f.regulatory.every((flag) => detectedFlags.includes(flag))
      if (same) return f
      return { ...f, regulatory: detectedFlags }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detected.regulatory.map((r) => r.flag).join(','), form.regulatoryUserEdited])

  const canContinue1 = form.name.trim().length >= 2 && form.problem_statement.trim().length >= 30
  const canContinue2 = form.shape !== null
  const canSubmit = canContinue1 && canContinue2 && form.audience.length > 0

  // -------------------------------------------------------------------- //
  // Submit pipeline
  // -------------------------------------------------------------------- //
  const submit = useMutation({
    mutationFn: async () => {
      setSubmitError(null)
      if (!form.shape) throw new Error('Pick a project shape before submitting.')
      if (form.audience.length === 0) throw new Error('Select at least one audience.')
      // Strict-mode intake validator (AI_ALM_INTAKE_OWNER_STRICT, on
      // by default) requires both fields whenever ANY non-`'none'`
      // regulatory flag is set. Catch this client-side so the user
      // doesn't have to round-trip a 422 to discover the gap.
      const hasComplianceFlag = form.regulatory.some(
        (r) => r && r !== 'none',
      )
      if (hasComplianceFlag) {
        if (!form.document_owner.trim()) {
          throw new Error(
            'Document owner is required for compliance-flagged projects. Fill it in Step 4 (Document control) or remove the compliance flag.',
          )
        }
        if (!form.document_approver.trim()) {
          throw new Error(
            'Document approver is required for compliance-flagged projects. Fill it in Step 4 (Document control) or remove the compliance flag.',
          )
        }
      }
      // AI-governance ratification: if the architect picked a
      // disposition, the ratifier name is required (else the backend
      // builder rejects the pair and the file is never written —
      // PEG-AI-GOVERNANCE-SIGNATURE-001 then fires at export).
      if (
        form.ai_governance_disposition &&
        !form.ai_governance_ratified_by.trim()
      ) {
        throw new Error(
          'AI governance ratifier name is required when a disposition is picked. Fill it in Step 3 (Compliance · AI governance) or clear the disposition.',
        )
      }

      // 1. Create project
      const project = await api.createProject({
        name: form.name.trim(),
        problem_statement: form.problem_statement.trim(),
      })

      // 1b. Upload any documents the user queued in Step 1 — content
      //     docs feed Phase 2 (Observer); format docs inform Phase 5
      //     export styling. We upload sequentially so a single bad
      //     file doesn't tank the whole batch and we can surface a
      //     useful error if the backend rejects something.
      const uploadFailures: string[] = []
      for (const pf of form.contentFiles) {
        try {
          await api.uploadInput(project.id, 'content', pf.file)
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : (err as Error).message
          uploadFailures.push(`content "${pf.file.name}" — ${msg}`)
        }
      }
      for (const pf of form.formatFiles) {
        try {
          await api.uploadInput(project.id, 'format', pf.file)
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : (err as Error).message
          uploadFailures.push(`format "${pf.file.name}" — ${msg}`)
        }
      }
      if (uploadFailures.length > 0) {
        // Don't block project creation on upload failures — surface
        // a warning toast-style in the error banner instead so the
        // user can re-upload from the Command Center inspector.
        setSubmitError(
          `Project created, but ${uploadFailures.length} upload(s) failed: ${uploadFailures
            .slice(0, 3)
            .join(' · ')}${uploadFailures.length > 3 ? ' …' : ''}`,
        )
      }

      // 2. Submit intake — translate the UX v0.7 shape to the legacy
      //    Domain field for back-compat AND send the new ``shape`` /
      //    ``recipe`` fields so the backend's recipe registry can
      //    enforce the agent set Phase 3 actually runs (see
      //    `brain/15-workflow-recipes.md`).
      //
      //    Without these new fields the Phase-3 swarm would still run
      //    every agent regardless of the UI choice — which was the
      //    bug that motivated this round of changes.
      const shape = getShape(form.shape)
      const legacyRegulatory =
        form.regulatory.length > 0 ? form.regulatory : (['none'] as RegulatoryFlag[])
      await api.submitIntake(project.id, {
        project_id: project.id,
        role: 'solutions_architect', // legacy-required; deprecated in new UI.
        domain: shape.domain,
        domain_other: shape.domain === 'other' ? shape.label : null,
        regulatory: legacyRegulatory,
        // ``audience`` (singular) keeps the legacy single-bucket field
        // populated for back-compat — code paths that pre-date the
        // multi-select read it. ``audience_tags`` is the real signal:
        // the backend resolver applies one overlay per tag on top of
        // the base template, so picking "Designers + Auditors" now
        // produces a meaningfully different export from picking
        // either alone (see services/audience_overlays.py).
        audience: collapseAudience(form.audience),
        audience_tags: form.audience,
        intervention_budget: form.cadence,
        output_template: { mode: 'system_default' },
        shape: form.shape,
        recipe: shape.recipe,
        // Document-control owner/approver. Strict-mode intake
        // validator requires both when ANY non-'none' regulatory
        // flag is set; we send null when empty + no flags so legacy
        // (no-compliance) projects still validate.
        document_owner: form.document_owner.trim() || null,
        document_approver: form.document_approver.trim() || null,
        // AI governance ratification. Send only when the architect
        // picked a disposition + a ratifier (the backend builder in
        // `ai_governance_decision.build_decision_from_intake` is
        // null-tolerant; an unset disposition leaves the file
        // unwritten and the PEG free to fire at export — by design).
        ai_governance_disposition: form.ai_governance_disposition,
        ai_governance_ratified_by:
          form.ai_governance_ratified_by.trim() || null,
        ai_governance_rationale:
          form.ai_governance_rationale.trim() || null,
        // Deployment architecture — closes PEG-DEPLOYMENT-MANIFEST-001
        // when populated. Empty strings collapse to null so the
        // backend's `IntakeAnswers` accepts the missing-field case
        // unchanged (each field is `str | None` with default `None`).
        // The Operations Architect agent's prompt reads these
        // verbatim; deployment_manifest_postprocessor.fill_tbd_
        // decisions_from_intake is the safety net behind that.
        cloud_provider: form.cloud_provider.trim() || null,
        primary_region: form.primary_region.trim() || null,
        container_runtime: form.container_runtime.trim() || null,
        orchestrator: form.orchestrator.trim() || null,
        iac_framework: form.iac_framework.trim() || null,
        secrets_store: form.secrets_store.trim() || null,
        secrets_rotation_policy:
          form.secrets_rotation_policy.trim() || null,
        oncall_primary_rotation:
          form.oncall_primary_rotation.trim() || null,
        oncall_runbook_index: form.oncall_runbook_index.trim() || null,
        oncall_pager_severity_thresholds:
          form.oncall_pager_severity_thresholds.trim() || null,
      })

      // 3. Persist the new-UI fields client-side so Command Center can
      //    show the richer shape/audience context.
      setProjectShape(project.id, form.shape)
      setProjectAudience(project.id, form.audience)
      setProjectVertical(project.id, form.vertical)

      // 4. Emit the Phase-1 briefing so the Command Center lands with
      //    a "briefing pending" focus state (no LLM spend yet).
      try {
        await api.createBriefing(project.id, 1, 1)
      } catch {
        // Idempotent on backend.
      }

      return project
    },
    onSuccess: (project) => {
      // Clear the persisted draft — we have a real project now and
      // the user will start fresh next time they hit "+ New project".
      clearDraft()
      void clearPendingFiles('intake')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate(`/projects/${project.id}`)
    },
    onError: (err) => {
      setSubmitError(err instanceof ApiError ? err.message : (err as Error).message)
    },
  })

  // -------------------------------------------------------------------- //

  const draftMeaningful =
    form.name.trim().length > 0 || form.problem_statement.trim().length > 0

  return (
    <div className="min-h-[calc(100vh-96px)] text-slate-100">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white"
              title="Discard the draft and return to the Hub"
            >
              <ArrowLeft className="h-4 w-4" /> Cancel
            </button>
            {draftMeaningful ? (
              <button
                type="button"
                onClick={saveAndExit}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-white/5"
                title="Save your work locally and return to the Hub — resume later from the Hub"
              >
                <Save className="h-3.5 w-3.5" /> Save & exit
              </button>
            ) : null}
          </div>
          <div className="text-xs text-slate-400">
            Step <span className="text-white font-semibold">{step}</span> of 3 ·{' '}
            <span className="text-slate-500">
              {step === 1 ? 'Describe' : step === 2 ? 'Shape' : 'Delivery'}
            </span>
          </div>
        </div>

        {/* Resume banner — surfaces the saved draft with a friendly age
            so the user can decide whether to pick up where they left off. */}
        {draftPrompt === 'pending' && persistedDraft ? (
          <ResumeDraftBanner
            draft={persistedDraft}
            onResume={resumeDraft}
            onDiscard={discardDraft}
          />
        ) : null}

        {/* Stepper */}
        <StepperBar step={step} />

        {/* Step panels */}
        {step === 1 && (
          <StepDescribe
            form={form}
            onChange={setForm}
            onContinue={() => canContinue1 && setStep(2)}
            onContentFilesChange={(next) =>
              setForm((f) => ({ ...f, contentFiles: next }))
            }
            onFormatFilesChange={(next) =>
              setForm((f) => ({ ...f, formatFiles: next }))
            }
          />
        )}
        {step === 2 && (
          <StepShape
            form={form}
            detected={detected}
            onChange={setForm}
            onBack={() => setStep(1)}
            onContinue={() => canContinue2 && setStep(3)}
          />
        )}
        {step === 3 && (
          <StepCompliance
            form={form}
            onChange={setForm}
            onBack={() => setStep(2)}
            onContinue={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <StepDelivery
            form={form}
            onChange={setForm}
            onBack={() => setStep(3)}
            onSubmit={() => canSubmit && submit.mutate()}
            submitting={submit.isPending}
            submitError={submitError}
          />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------- //
// StepperBar
// ---------------------------------------------------------------------- //
function StepperBar({ step }: { step: 1 | 2 | 3 | 4 }) {
  const rows: Array<{ n: 1 | 2 | 3 | 4; title: string; sub: string }> = [
    { n: 1, title: 'Describe', sub: 'Problem + context' },
    { n: 2, title: 'Shape', sub: 'What kind of project' },
    { n: 3, title: 'Compliance', sub: 'Regulatory cues' },
    { n: 4, title: 'Delivery', sub: 'Audience & oversight' },
  ]
  return (
    <div className="flex items-center gap-2 mb-7">
      {rows.map((row, i) => {
        const klass =
          step === row.n ? 'alm-step-active' : row.n < step ? 'alm-step-done' : 'alm-step-todo'
        return (
          <div key={row.n} className="flex items-center gap-3 flex-1">
            <span className={`alm-step-dot ${klass}`}>
              {row.n < step ? <Check className="h-4 w-4" /> : row.n}
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-100 leading-tight">{row.title}</div>
              <div className="text-[11px] text-slate-500">{row.sub}</div>
            </div>
            {i < rows.length - 1 && <div className="h-px flex-1 bg-white/10 mx-1" />}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// Step 1 — Describe
// ---------------------------------------------------------------------- //
interface DetectedPayload {
  shape: ReturnType<typeof detectShape>
  regulatory: ReturnType<typeof detectRegulatory>
  nouns: string[]
}

function StepDescribe({
  form,
  onChange,
  onContinue,
  onContentFilesChange,
  onFormatFilesChange,
}: {
  form: FormState
  onChange: React.Dispatch<React.SetStateAction<FormState>>
  onContinue: () => void
  onContentFilesChange: (next: PendingFile[]) => void
  onFormatFilesChange: (next: PendingFile[]) => void
}) {
  const canContinue =
    form.name.trim().length >= 2 && form.problem_statement.trim().length >= 30
  const count = form.problem_statement.length
  const hint =
    count < 30
      ? `add ${30 - count}+ chars to continue`
      : count < 80
      ? 'good — add more detail for better Phase-1 extraction'
      : 'looks great'

  return (
    <section
      data-testid="intake-step-1"
      className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-7 alm-fadein"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-1">
        <Edit3 className="h-4 w-4" /> Describe the project
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-2">What are we building?</h2>
      <p className="text-slate-400 mb-5 text-sm">
        Write it like you'd brief a sharp engineer. A paragraph or two is plenty — we'll extract
        nouns and propose clarifying questions in Phase 1.
      </p>

      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Project name
        </label>
        <input
          value={form.name}
          onChange={(e) => onChange((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Edge application for robot navigation"
          maxLength={200}
          className="mt-1.5 w-full bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Problem statement
          </label>
          <span className="text-[11px] text-slate-500">
            — {count} chars · {hint}
          </span>
        </div>
        <textarea
          value={form.problem_statement}
          onChange={(e) => onChange((f) => ({ ...f, problem_statement: e.target.value }))}
          rows={7}
          placeholder='e.g. "An autonomous indoor navigation stack for mobile robots. The system uses on-device SLAM (visual + LiDAR odometry), real-time YOLO inference on NPU hardware, and a reactive local planner to avoid moving obstacles. Must operate offline when Wi-Fi drops, with latency under 20 ms for emergency braking."'
          className="w-full bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        />
      </div>

      {/* Upload zones — Phase-2 content docs + Phase-5 format hints. */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <InputUploader
          mode="pending"
          kind="content"
          title="Supporting documents"
          description="Specs, vendor PDFs, schemas — fed to Phase 2 (Observer) for constraint extraction."
          hint="Optional · Markdown, PDF, DOCX, JSON, YAML, CSV, images"
          files={form.contentFiles}
          onChange={onContentFilesChange}
        />
        <InputUploader
          mode="pending"
          kind="format"
          title="Formatting instructions"
          description="Sample requirement docs, brand guidelines, RFP templates — informs Phase 5 export styling."
          hint="Optional · same file types as content uploads"
          files={form.formatFiles}
          onChange={onFormatFilesChange}
        />
      </div>

      <div className="mb-5 mt-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
          Industry vertical <span className="normal-case tracking-normal text-slate-500">(optional — gates compliance content)</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {VERTICALS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange((f) => ({ ...f, vertical: f.vertical === v ? null : v }))}
              className={`alm-chip ${form.vertical === v ? 'alm-chip-accent' : ''}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <div className="text-[11px] text-slate-500">
          We never send this to an LLM until you approve the Phase 1 briefing.
        </div>
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------- //
// Step 2 — Shape
// ---------------------------------------------------------------------- //
function StepShape({
  form,
  detected,
  onChange,
  onBack,
  onContinue,
}: {
  form: FormState
  detected: DetectedPayload,
  onChange: React.Dispatch<React.SetStateAction<FormState>>
  onBack: () => void
  onContinue: () => void
}) {
  const selected = form.shape ? getShape(form.shape) : null

  return (
    <section
      data-testid="intake-step-2"
      className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-7 alm-fadein"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-1">
        <Shapes className="h-4 w-4" /> Project shape
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-2">
        What kind of solution are we building?
      </h2>
      <p className="text-slate-400 mb-5 text-sm">
        This picks the <strong className="text-indigo-300">workflow recipe</strong> — which
        agents run, which rules the Critic applies, which template is the default.
      </p>

      <div className="mb-5">
        <SelectableCardGrid
          options={PROJECT_SHAPES.map((s) => ({
            id: s.id,
            label: s.label,
            hint: s.hint,
            Icon:
              (LucideIcons as unknown as Record<
                string,
                React.ComponentType<{ className?: string }>
              >)[toLucideKey(s.icon)] ?? LucideIcons.Circle,
          }))}
          value={form.shape ? [form.shape] : []}
          onChange={(ids) => onChange((f) => ({ ...f, shape: (ids[0] as ShapeId) ?? null }))}
        />
      </div>

      {/* Recipe preview */}
      {selected && (
        <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-4 mb-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
            <Workflow className="h-3 w-3 text-indigo-400" /> Routed to recipe:{' '}
            <span className="text-indigo-300 normal-case tracking-normal font-semibold">
              {selected.recipe}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selected.agents.map((agent, i) => (
              <span
                key={agent}
                className={`alm-chip ${i >= 5 ? 'alm-chip-info' : 'alm-chip-ok'}`}
              >
                {i >= 5 ? <Plus className="h-3 w-3" /> : null}
                {agent}
              </span>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            {selected.agents.length} agent{selected.agents.length === 1 ? '' : 's'} will run in
            Phase 3 · swarm width adapts to recipe
          </div>
        </div>
      )}

      {/* Detection panel */}
      {form.problem_statement.trim().length >= 30 && (
        <div className="mb-5 p-4 rounded-lg border border-slate-700/60 bg-slate-950/40">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
            <ScanSearch className="h-3 w-3 text-indigo-400" /> Detected from your description
          </div>

          <div className="flex flex-wrap gap-2 mb-2 items-center">
            <span className="text-[11px] text-slate-500">Shape →</span>
            {detected.shape.shape ? (
              <>
                <span className="alm-chip alm-chip-info">
                  {detected.shape.shape.label}
                </span>
                <span className="text-[10px] text-slate-500">
                  {(detected.shape.score * 100).toFixed(0)}% confidence · you can change it in step 2
                </span>
              </>
            ) : (
              <span className="alm-chip text-slate-500">
                no strong signal yet · you'll pick in step 2
              </span>
            )}
          </div>

          {detected.nouns.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2 items-center">
              <span className="text-[11px] text-slate-500">Nouns to freeze →</span>
              {detected.nouns.map((n) => (
                <span key={n} className="alm-noun">
                  {n}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!form.shape}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------- //
// Step 3 — Compliance cues
// ---------------------------------------------------------------------- //
function StepCompliance({
  form,
  onChange,
  onBack,
  onContinue,
}: {
  form: FormState
  onChange: React.Dispatch<React.SetStateAction<FormState>>
  onBack: () => void
  onContinue: () => void
}) {
  return (
    <section
      data-testid="intake-step-3"
      className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-7 alm-fadein"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-1">
        <ShieldCheck className="h-4 w-4" /> Compliance cues
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-2">Which regulations apply?</h2>
      <p className="text-slate-400 mb-5 text-sm">
        Pick every framework this project touches - the{' '}
        <strong className="text-indigo-300">Compliance agent</strong> builds a risk register and
        evidence checklist for each one. Anything we detected from your description is pre-selected.
      </p>

      <div className="mb-5">
        <SelectableCardGrid
          multiple
          options={REGULATORY_CUES.map((c) => ({ id: c.flag, label: c.label }))}
          value={form.regulatory}
          onChange={(ids) =>
            onChange((f) => ({
              ...f,
              regulatory: ids as RegulatoryFlag[],
              regulatoryUserEdited: true,
            }))
          }
        />
      </div>

      {/* AI-governance ratification — pre-empts
          PEG-AI-GOVERNANCE-SIGNATURE-001. The gate fires on
          BR/SR text at export time whenever an AI-derived
          output is declared; so the block surfaces whenever
          the architect picked the ML/AI shape OR the problem
          statement carries detectable ML/AI cues OR an AI
          governance framework (NIST AI RMF / ISO 42001) was
          ticked in Q3 OR a disposition was already entered
          (sticky on resume).
          2026-05-28: the original ``form.shape === 'ml_ai'``
          gate missed projects whose primary shape was Web /
          SaaS but whose problem statement names AI features
          (RAG, predictive analytics, agentic triage, …) —
          exactly the medical-toxicology-application_dspk5ork
          shape that tripped the gate at Phase 5.
          2026-07-05: added the ``aiFlagPicked`` arm. Ticking
          NIST AI RMF / ISO 42001 in Q3 drives the Phase-2.5
          compliance pack (16 AI-governance stubs) on its own,
          but the ratification card only appeared for ML/AI
          shapes or cue hits — so a non-AI project (e.g. a
          drone/FCC app) could opt in and inject ~23 AI-gov
          requirements with NO confirmation, no ratifier, and
          disposition left null. Gating the card on the flag
          makes opting in a conscious, signed choice — or an
          obvious "unselect this, it's not an AI project". */}
      {(() => {
        const aiCueHits = detectShape(form.problem_statement).matches.ml_ai ?? 0
        const aiFlagPicked = form.regulatory.some(
          (r) => r === 'nist_ai_rmf' || r === 'iso_42001',
        )
        const showAiGovBlock =
          form.shape === 'ml_ai' ||
          aiCueHits > 0 ||
          aiFlagPicked ||
          form.ai_governance_disposition !== null
        return showAiGovBlock
      })() && (
        <div className="mt-4 mb-5 pt-4 border-t border-slate-700/40">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              AI governance · ratification
            </div>
            <div className="text-[10.5px] text-slate-500">
              Closes <code className="text-[10px]">PEG-AI-GOVERNANCE-SIGNATURE-001</code> proactively
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            {(() => {
              if (form.shape === 'ml_ai') {
                return (
                  <>
                    Your project is shaped as{' '}
                    <strong>ML / AI system</strong>.
                  </>
                )
              }
              const aiCueHits =
                detectShape(form.problem_statement).matches.ml_ai ?? 0
              if (aiCueHits > 0) {
                return (
                  <>
                    We detected <strong>AI signals</strong> in your
                    problem statement (RAG / model / predictive /
                    agentic / ML wording). The export gate
                    (<code className="text-[10px]">PEG-AI-GOVERNANCE-SIGNATURE-001</code>)
                    fires on AI-derived requirements regardless of
                    the picked shape, so we surface ratification here
                    to pre-empt it.
                  </>
                )
              }
              return (
                <>
                  You selected an <strong>AI governance framework</strong>{' '}
                  (NIST AI RMF / ISO 42001) above, which injects a set of
                  AI-governance requirements (model cards, bias / drift /
                  explainability). This project isn't otherwise detected
                  as an AI/ML system, so confirm it's genuinely in scope —
                  or <strong>unselect that framework above</strong> if this
                  project has no AI/ML element.
                </>
              )
            })()}{' '}
            Pick a disposition and sign it now so the export gate
            doesn't refuse you later. Both options are <em>signed</em>:
            <em> in scope</em> means you'll apply an AI governance
            program; <em>not applicable</em> means you've deliberated
            and affirmed governance does not apply to this scope.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() =>
                onChange((f) => ({
                  ...f,
                  ai_governance_disposition: 'in_scope',
                }))
              }
              className={`text-left p-3 rounded-md border ${
                form.ai_governance_disposition === 'in_scope'
                  ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-200'
                  : 'border-slate-700 hover:bg-white/5 text-slate-300'
              }`}
            >
              <div className="text-sm font-semibold">In scope</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                We'll apply an AI governance program (NIST AI RMF, model card, bias/drift monitoring, …).
              </div>
            </button>
            <button
              type="button"
              onClick={() =>
                onChange((f) => ({
                  ...f,
                  ai_governance_disposition: 'not_applicable',
                }))
              }
              className={`text-left p-3 rounded-md border ${
                form.ai_governance_disposition === 'not_applicable'
                  ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-200'
                  : 'border-slate-700 hover:bg-white/5 text-slate-300'
              }`}
            >
              <div className="text-sm font-semibold">Not applicable</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                The AI element doesn't trigger governance obligations in this scope (architect affirms).
              </div>
            </button>
          </div>
          {form.ai_governance_disposition && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="ai-ratified-by"
                  className="block text-[10.5px] uppercase tracking-wider text-slate-500 mb-1"
                >
                  Ratified by *
                </label>
                <input
                  id="ai-ratified-by"
                  type="text"
                  value={form.ai_governance_ratified_by}
                  onChange={(e) =>
                    onChange((f) => ({
                      ...f,
                      ai_governance_ratified_by: e.target.value,
                    }))
                  }
                  placeholder="e.g. Sam Lee, AI Governance Lead"
                  className={`w-full bg-slate-950/60 border rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60 ${
                    !form.ai_governance_ratified_by.trim()
                      ? 'border-amber-700/50'
                      : 'border-slate-700'
                  }`}
                />
              </div>
              <div>
                <label
                  htmlFor="ai-rationale"
                  className="block text-[10.5px] uppercase tracking-wider text-slate-500 mb-1"
                >
                  Rationale <span className="text-slate-600 normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  id="ai-rationale"
                  type="text"
                  value={form.ai_governance_rationale}
                  onChange={(e) =>
                    onChange((f) => ({
                      ...f,
                      ai_governance_rationale: e.target.value,
                    }))
                  }
                  placeholder={
                    form.ai_governance_disposition === 'not_applicable'
                      ? 'Why governance does not apply here'
                      : 'Brief context (e.g. NIST AI RMF tier)'
                  }
                  className="w-full bg-slate-950/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60"
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  )
}

// Lucide icon names in the registry are PascalCase (BrainCircuit, etc.).
function toLucideKey(kebab: string): string {
  return kebab
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('')
}

// ---------------------------------------------------------------------- //
// Step 4 — Delivery
// ---------------------------------------------------------------------- //
function StepDelivery({
  form,
  onChange,
  onBack,
  onSubmit,
  submitting,
  submitError,
}: {
  form: FormState
  onChange: React.Dispatch<React.SetStateAction<FormState>>
  onBack: () => void
  onSubmit: () => void
  submitting: boolean
  submitError: string | null
}) {
  const selected = form.shape ? getShape(form.shape) : null
  const gateMap: Record<InterventionBudget, string[]> = {
    minimal: ['G1', 'G4', 'G5'],
    standard: ['G1', 'G2', 'G4', 'G5'],
    thorough: ['G1', 'G2', 'G3', 'G4', 'G5'],
  }

  // -------------------------------------------------------------------- //
  // Deployment-architecture catalog (2026-05-28 — Tier 1 + Tier 2 behind
  // PEG-DEPLOYMENT-MANIFEST-001). React Query fetches the curated
  // catalog re-ranked by the backend for the project's current shape +
  // regulatory flags; it refetches whenever those change so the
  // dropdowns stay in lock-step with the wizard's other selections.
  // The query is gated to Step 3 (where the dropdowns render) so we
  // don't pay the round-trip until the user actually needs it.
  // -------------------------------------------------------------------- //
  const regulatoryKey = form.regulatory.join(',')
  const deploymentCatalogQuery = useQuery({
    queryKey: ['deploymentCatalog', form.shape, regulatoryKey],
    queryFn: () =>
      api.getDeploymentCatalog({
        shape: form.shape ?? undefined,
        regulatory: form.regulatory,
      }),
    staleTime: 5 * 60 * 1000,
  })
  const catalogFieldsById: Record<string, DeploymentCatalogField> = useMemo(
    () => {
      const out: Record<string, DeploymentCatalogField> = {}
      for (const f of deploymentCatalogQuery.data?.fields ?? []) {
        out[f.field_id] = f
      }
      return out
    },
    [deploymentCatalogQuery.data],
  )
  const llmContext = useMemo(
    () => ({
      problem_statement: form.problem_statement,
      shape: form.shape,
      regulatory: form.regulatory,
    }),
    [form.problem_statement, form.shape, form.regulatory],
  )
  type DeploymentFieldId =
    | 'cloud_provider'
    | 'primary_region'
    | 'container_runtime'
    | 'orchestrator'
    | 'iac_framework'
    | 'secrets_store'
    | 'secrets_rotation_policy'
    | 'oncall_primary_rotation'
    | 'oncall_runbook_index'
    | 'oncall_pager_severity_thresholds'
  const renderDeploymentField = (
    fieldId: DeploymentFieldId,
    label: string,
    placeholder: string,
  ) => {
    const block = catalogFieldsById[fieldId]
    const entries: DeploymentCatalogEntry[] = block?.entries ?? []
    return (
      <DeploymentField
        fieldId={fieldId}
        label={label}
        description={block?.description}
        catalogEntries={entries}
        value={form[fieldId]}
        onChange={(next) =>
          onChange((f) => ({ ...f, [fieldId]: next }))
        }
        placeholder={placeholder}
        llmContext={llmContext}
      />
    )
  }

  const toggleAudience = (tag: AudienceTag) => {
    onChange((f) => ({
      ...f,
      audience: f.audience.includes(tag)
        ? f.audience.filter((x) => x !== tag)
        : [...f.audience, tag],
    }))
  }

  // Mirror the backend ``select_base_template`` ladder so the Ready
  // summary tells the truth about which Template (A-E) the export will
  // start from. Overlay count = audience tags selected; the resolver
  // applies one overlay per tag on top of the base.
  const templateSummary = (() => {
    const tags = form.audience
    if (tags.length === 0) return 'Template A · default'
    let base: string
    if (tags.includes('auditors')) base = 'C (auditor)'
    else if (tags.includes('execs')) base = 'B (executive)'
    else if (tags.includes('procurement')) base = 'D (procurement)'
    else if (tags.length === 1) base = 'A (engineering)'
    else base = 'E (mixed)'
    return `Template ${base} · ${tags.length} overlay${tags.length === 1 ? '' : 's'}`
  })()

  return (
    <section
      data-testid="intake-step-4"
      className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-7 alm-fadein"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-1">
        <Send className="h-4 w-4" /> Delivery
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-2">
        Who's reading this, and how involved do you want to be?
      </h2>
      <p className="text-slate-400 mb-5 text-sm">
        Pick any number of audiences — we blend the tone. Pick a review cadence that matches your
        risk appetite.
      </p>

      <div className="grid grid-cols-2 gap-5 mb-5">
        {/* Audience */}
        <div className="p-4 rounded-lg border border-slate-700/60 bg-slate-950/40">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Audience · multi-select
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {AUDIENCE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleAudience(opt.id)}
                title={opt.hint}
                className={`alm-chip ${form.audience.includes(opt.id) ? 'alm-chip-accent' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500">
            {form.audience.length === 0
              ? 'Pick at least one.'
              : `Tone will blend: ${form.audience
                  .map((t) => AUDIENCE_OPTIONS.find((x) => x.id === t)?.label ?? t)
                  .join(' · ')}`}
          </div>
        </div>

        {/* Cadence */}
        <div className="p-4 rounded-lg border border-slate-700/60 bg-slate-950/40">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Review cadence
          </div>
          <div className="space-y-1.5">
            {(['minimal', 'standard', 'thorough'] as InterventionBudget[]).map((opt) => (
              <button
                type="button"
                key={opt}
                onClick={() => onChange((f) => ({ ...f, cadence: opt }))}
                className={`w-full text-left flex items-center gap-2 p-2 rounded-md ${
                  form.cadence === opt
                    ? 'bg-indigo-500/10 ring-1 ring-indigo-500/50'
                    : 'hover:bg-white/5'
                }`}
              >
                <span
                  className={`h-3.5 w-3.5 rounded-full border ${
                    form.cadence === opt
                      ? 'border-indigo-400 bg-indigo-500'
                      : 'border-slate-600'
                  }`}
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold capitalize text-slate-100">
                    {opt}{' '}
                    {opt === 'standard' && (
                      <span className="text-[10px] text-emerald-300 font-normal ml-1">
                        · default
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    {gateMap[opt].join(' · ')} —{' '}
                    {opt === 'minimal'
                      ? 'ship fast, low touch'
                      : opt === 'standard'
                      ? 'balanced review'
                      : 'regulated work'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Document control — owner + approver. Required by the
          backend's strict-mode intake validator whenever ANY
          non-`'none'` regulatory flag is set. We always render the
          fields but flag them as required only when compliance is on,
          so non-regulated projects can leave them blank. */}
      {(() => {
        const hasComplianceFlag = form.regulatory.some(
          (r) => r && r !== 'none',
        )
        return (
          <div className="p-4 rounded-lg border border-slate-700/60 bg-slate-950/40 mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Document control
                {hasComplianceFlag && (
                  <span className="ml-2 text-[10px] text-amber-300 normal-case tracking-normal">
                    · required (compliance flag set)
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-slate-500">
                Lands in the requirements doc header
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="doc-owner"
                  className="block text-[10.5px] uppercase tracking-wider text-slate-500 mb-1"
                >
                  Document owner{hasComplianceFlag ? ' *' : ''}
                </label>
                <input
                  id="doc-owner"
                  type="text"
                  value={form.document_owner}
                  onChange={(e) =>
                    onChange((f) => ({ ...f, document_owner: e.target.value }))
                  }
                  placeholder="e.g. Jane Doe, Director of Engineering"
                  className={`w-full bg-slate-950/60 border rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60 ${
                    hasComplianceFlag && !form.document_owner.trim()
                      ? 'border-amber-700/50'
                      : 'border-slate-700'
                  }`}
                />
              </div>
              <div>
                <label
                  htmlFor="doc-approver"
                  className="block text-[10.5px] uppercase tracking-wider text-slate-500 mb-1"
                >
                  Document approver{hasComplianceFlag ? ' *' : ''}
                </label>
                <input
                  id="doc-approver"
                  type="text"
                  value={form.document_approver}
                  onChange={(e) =>
                    onChange((f) => ({
                      ...f,
                      document_approver: e.target.value,
                    }))
                  }
                  placeholder="e.g. John Smith, VP of Compliance"
                  className={`w-full bg-slate-950/60 border rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60 ${
                    hasComplianceFlag && !form.document_approver.trim()
                      ? 'border-amber-700/50'
                      : 'border-slate-700'
                  }`}
                />
              </div>
            </div>
            {hasComplianceFlag && (
              <p className="text-[11px] text-slate-500 mt-2">
                SOC 2 / ISO 27001 reviewers require a named owner and
                approver before treating the requirements package as
                <em> issued</em> vs <em>draft</em>. The per-role
                signatory chain (CISO, DPO, …) is filled separately in
                the Approval Chain editor inside the Command Center.
              </p>
            )}

            {/* Deployment architecture — 2026-05-28 — closes
                PEG-DEPLOYMENT-MANIFEST-001 proactively. The gate
                requires 10 deployment-architecture decisions for
                SOC 2 CC7 / CC8 evidence. Each field is OPTIONAL —
                leaving it blank is a valid "deliberately deferred"
                choice; the gate will list missing keys at Phase 5
                and the architect either fills them post-hoc or
                waives via phase4/audit-diff.waivers.json. Grouped
                into four panels mirroring the manifest schema
                (topology / runtime+IaC / secrets / on-call). */}
            <div className="mt-4 pt-4 border-t border-slate-700/40">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Project architecture
                  <span className="ml-2 text-[10px] text-slate-500 normal-case tracking-normal">
                    · optional
                  </span>
                </div>
                <div className="text-[10.5px] text-slate-500">
                  Closes <code className="text-[10px]">PEG-DEPLOYMENT-MANIFEST-001</code> proactively
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mb-3">
                The Operations Architect agent reads these verbatim into the
                deployment manifest. Leave any field blank to defer the
                decision — the export gate will list missing keys for
                ratification or waiver at Phase 5. SOC 2 CC7 (system
                operations) / CC8 (change management) audits expect these
                values to land in the manifest before export.
              </p>

              {/* Topology */}
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mt-3 mb-2">
                Topology
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {renderDeploymentField(
                  'cloud_provider',
                  'Cloud provider',
                  'e.g. AWS HIPAA-eligible accounts',
                )}
                {renderDeploymentField(
                  'primary_region',
                  'Primary region',
                  'e.g. us-east-1',
                )}
              </div>

              {/* Runtime + IaC */}
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
                Runtime &amp; infrastructure-as-code
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {renderDeploymentField(
                  'container_runtime',
                  'Container runtime',
                  'e.g. containerd',
                )}
                {renderDeploymentField(
                  'orchestrator',
                  'Orchestrator',
                  'e.g. EKS / ECS / Cloud Run',
                )}
                {renderDeploymentField(
                  'iac_framework',
                  'IaC framework',
                  'e.g. Terraform 1.8',
                )}
              </div>

              {/* Secrets */}
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
                Secrets &amp; key management
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {renderDeploymentField(
                  'secrets_store',
                  'Secrets store',
                  'e.g. AWS Secrets Manager',
                )}
                {renderDeploymentField(
                  'secrets_rotation_policy',
                  'Rotation policy',
                  'e.g. 90 days',
                )}
              </div>

              {/* On-call */}
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
                On-call &amp; operations
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {renderDeploymentField(
                  'oncall_primary_rotation',
                  'Primary on-call rotation',
                  'e.g. Platform Engineering',
                )}
                {renderDeploymentField(
                  'oncall_runbook_index',
                  'Runbook index',
                  'e.g. docs/runbooks/index.md',
                )}
                {renderDeploymentField(
                  'oncall_pager_severity_thresholds',
                  'Pager severity thresholds',
                  'e.g. P1≤5m, P2≤30m',
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* Ready summary */}
      <div className="rounded-lg p-4 mb-5 border border-cyan-700/40 bg-gradient-to-b from-cyan-950/40 to-slate-950/40">
        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-cyan-300">
          <CheckCircle2 className="h-4 w-4" /> Ready to run
        </div>
        <div className="text-xs text-slate-300 leading-relaxed">
          <strong className="text-white">{form.name || 'Untitled project'}</strong> ·{' '}
          {selected?.label ?? '—'}
          {form.vertical ? ` · ${form.vertical}` : ''} ·{' '}
          {form.audience.length} audience{form.audience.length === 1 ? '' : 's'} ·{' '}
          <span className="capitalize">{form.cadence}</span> cadence · {templateSummary}
        </div>
      </div>

      {submitError && (
        <div className="rounded-md border border-red-700/50 bg-red-950/40 p-3 text-xs text-red-200 mb-4 flex items-start gap-2">
          <X className="h-4 w-4 shrink-0 mt-0.5" /> {submitError}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5"
          disabled={submitting}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          type="button"
          data-testid="intake-submit"
          onClick={onSubmit}
          disabled={submitting || form.audience.length === 0}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Creating project…
            </>
          ) : (
            <>
              {/* Audit §1.7 — copy used to read "Create project & brief
                  Phase 1" but a successful submit lands on the Command
                  Center with the briefing in `pending`. The briefing
                  artifact is emitted but the LLM phase has not run, so
                  "open Command Center" is the honest description. */}
              Create project & open Command Center{' '}
              <Sparkles className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------- //
// ResumeDraftBanner — surfaced at the top of the wizard when the user
// arrives with a previously-saved draft. Plan §P2.16. The banner is
// dismissed once the user picks Resume or Discard, after which the
// auto-save effect takes over.
// ---------------------------------------------------------------------- //
function ResumeDraftBanner({
  draft,
  onResume,
  onDiscard,
}: {
  draft: IntakeDraft
  onResume: () => void
  onDiscard: () => void
}) {
  const summary = draft.name.trim() || 'Untitled draft'
  const subline = draft.problem_statement.trim().slice(0, 140)
  return (
    <div className="rounded-lg border border-cyan-700/40 bg-gradient-to-b from-cyan-950/40 to-slate-950/40 p-4 mb-5 alm-fadein">
      <div className="flex items-start gap-3">
        <RotateCcw className="h-5 w-5 text-cyan-300 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-cyan-200">
            Resume your saved draft?
          </div>
          <div className="text-xs text-slate-300 mt-1 leading-relaxed">
            <span className="font-semibold text-slate-100">{summary}</span>
            {' · '}
            saved {formatRelativeAge(draft.savedAt) || 'recently'}
            {' · '}
            step {draft.step} of 3
          </div>
          {subline ? (
            <div className="text-[11px] text-slate-500 mt-1 line-clamp-2 italic">
              "{subline}{draft.problem_statement.length > 140 ? '…' : ''}"
            </div>
          ) : null}
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={onResume}
              className="inline-flex items-center gap-1.5 rounded-md bg-cyan-500 hover:bg-cyan-400 px-3 py-1.5 text-xs font-semibold text-cyan-950"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Resume
            </button>
            <button
              type="button"
              onClick={onDiscard}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5"
            >
              <X className="h-3.5 w-3.5" /> Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

